import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Briefcase,
  FileText,
  Heart,
  Menu,
  ShieldAlert,
  User,
  X,
} from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';

/**
 * Candidate dashboard sidebar. Two presentations:
 *   - Desktop (md+): always-visible 240px column with the website's brand chrome.
 *   - Mobile (<md): off-canvas drawer triggered by the hamburger button rendered in
 *     `<DashboardMobileTrigger>`. Backdrop click and ESC close it.
 *
 * Why not the shared `<Sidebar>` primitive directly? The website dashboard sits BELOW
 * the existing Navbar/Footer chrome (analysis §5.2) — it must NOT take the full viewport
 * the way admin/customer apps do via `<AppShell>`. The shared Sidebar's hard-coded
 * `flex flex-col` + collapse chrome don't fit a sub-page panel that renders inside
 * the marketing-shell scroll container, so we render a website-tuned variant here.
 */

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  end?: boolean;
}

const PRIMARY_NAV: NavItem[] = [
  { to: '/dashboard/profile', label: 'My Profile', icon: User },
  { to: '/dashboard/resumes', label: 'My Resumes', icon: FileText },
  { to: '/dashboard/applications', label: 'My Applications', icon: Briefcase },
  { to: '/dashboard/saved-jobs', label: 'Saved Jobs', icon: Heart },
  // Preferences nav item hidden per request 2026-05-12. Route + page remain
  // functional and reachable via direct URL (`/dashboard/preferences`);
  // uncomment to restore visibility in the sidebar.
  // { to: '/dashboard/preferences', label: 'Preferences', icon: Settings },
];

const FOOTER_NAV: NavItem[] = [
  { to: '/dashboard/account', label: 'Account', icon: ShieldAlert },
];

function NavList({ items, onClick }: { items: NavItem[]; onClick?: () => void }) {
  return (
    <nav className="flex flex-col gap-0.5">
      {items.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onClick}
          className={({ isActive }) =>
            [
              'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold transition-colors',
              isActive
                ? 'bg-brand-secondary text-brand-deep'
                : 'text-brand-muted hover:bg-gray-50 hover:text-brand-primary',
            ].join(' ')
          }
        >
          <Icon className="w-4 h-4 shrink-0" />
          <span className="truncate">{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarPanel({ onItemClick }: { onItemClick?: () => void }) {
  return (
    <div className="flex flex-col h-full">
      <div className="px-2 py-1">
        <p className="px-3 pb-2 text-[10px] font-black uppercase tracking-[0.2em] text-brand-muted">
          Dashboard
        </p>
        <NavList items={PRIMARY_NAV} onClick={onItemClick} />
      </div>
      <div className="px-2 py-3 mt-auto border-t border-gray-100">
        <NavList items={FOOTER_NAV} onClick={onItemClick} />
      </div>
    </div>
  );
}

export function DashboardSidebarDesktop() {
  return (
    <aside
      aria-label="Dashboard sections"
      className="hidden md:flex w-60 shrink-0 bg-white border border-gray-100 rounded-3xl shadow-sm sticky top-28 self-start min-h-[400px]"
    >
      <div className="flex-1 py-3">
        <SidebarPanel />
      </div>
    </aside>
  );
}

interface DrawerProps {
  open: boolean;
  onClose: () => void;
}

export function DashboardSidebarDrawer({ open, onClose }: DrawerProps) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[60] bg-black/40 md:hidden"
            onClick={onClose}
            aria-hidden
          />
          <motion.aside
            role="dialog"
            aria-modal
            aria-label="Dashboard menu"
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-y-0 left-0 z-[61] w-72 max-w-[80vw] bg-white shadow-2xl flex flex-col md:hidden"
          >
            <div className="flex items-center justify-between px-4 h-14 border-b border-gray-100">
              <p className="text-sm font-display font-bold text-brand-primary">Dashboard</p>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close menu"
                className="p-1 text-gray-400 hover:text-brand-primary transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto py-2">
              <SidebarPanel onItemClick={onClose} />
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

interface MobileBarProps {
  onOpen: () => void;
  /** Currently-active section title; rendered next to the hamburger for orientation. */
  activeLabel: ReactNode;
}

export function DashboardMobileBar({ onOpen, activeLabel }: MobileBarProps) {
  return (
    <div className="flex md:hidden items-center gap-3 px-1 mb-4">
      <button
        type="button"
        onClick={onOpen}
        aria-label="Open dashboard menu"
        className="p-2 -ml-2 rounded-xl text-brand-primary hover:bg-gray-100 transition-colors"
      >
        <Menu className="w-5 h-5" />
      </button>
      <p className="text-xs font-black uppercase tracking-[0.2em] text-brand-muted">
        {activeLabel}
      </p>
    </div>
  );
}

export function useDashboardDrawer(): {
  open: boolean;
  setOpen: (next: boolean) => void;
} {
  const [open, setOpen] = useState(false);
  return { open, setOpen };
}

export const DASHBOARD_NAV_LABELS: Record<string, string> = Object.fromEntries(
  [...PRIMARY_NAV, ...FOOTER_NAV].map((item) => [item.to, item.label]),
);

export function DashboardSignInPrompt() {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 text-center max-w-lg mx-auto">
      <h2 className="text-xl font-display font-bold text-brand-primary mb-2">
        Sign in to access your dashboard
      </h2>
      <p className="text-sm text-brand-muted mb-6">
        Your dashboard shows your profile, resumes, applications, and saved jobs.
      </p>
      <Link
        to="/signin?return=%2Fdashboard%2Fprofile"
        className="inline-flex items-center gap-2 px-6 py-3 bg-brand-primary text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-brand-deep transition-all"
      >
        Sign in
      </Link>
    </div>
  );
}
